import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  forgotPasswordAPI,
  login,
  logout,
  resetPasswordAPI,
} from "../services/authServices"; // Import the login service

interface AuthState {
  user: { id: number; email: string; name: string; role: string } | null; // User info
  token: string | null; // Access token
  loading: boolean; // Loading state
  error: string | null; // Error message
  message: string | null;
}

const initialState: AuthState = {
  user: null,
  token: null,
  loading: false,
  error: null,
  message:null,
};
export interface LogoutResponse {
  status: boolean;
  status_code: number;
  message: string | null;
}
// logout user
export const logoutUser = createAsyncThunk<
  LogoutResponse, // ✅ correct return type
  void,
  { rejectValue: string }
>("auth/logoutUser", async (_, { rejectWithValue }) => {
  try {
    const response = await logout();
    if (response.status) {
      return response;
    } else {
      return rejectWithValue(response.message || "Logout failed");
    }
  } catch (error: any) {
    return rejectWithValue(error.message || "Logout failed");
  }
});

// forgot password
export const forgotPassword = createAsyncThunk<
  LogoutResponse, // ✅ Use LogoutResponse as return type
  string, // Argument: email
  { rejectValue: string } // Rejection type
>("auth/forgotPassword", async (email, { rejectWithValue }) => {
  try {
    const response = await forgotPasswordAPI(email);
    return response;
  } catch (err: any) {
    return rejectWithValue(
      err.message || "Failed to send password reset email"
    );
  }
});

// Async thunk for login
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (
    {
      email,
      password,
      is_web,
    }: { email: string; password: string; is_web?: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await login(email, password, is_web); // response from API

      if (response.status) {
        return response; // success case
      } else {
        return rejectWithValue(response.message || "Login failed"); // failure from API
      }
    } catch (error: any) {
      return rejectWithValue(error.message || "Login failed");
    }
  }
);

// Reset Password
export const resetPassword = createAsyncThunk<
  LogoutResponse, // ✅ Return type
  { email: string; password: string; token: string }, // ✅ Args
  { rejectValue: string } // ✅ Rejection
>("auth/resetPassword", async (payload, { rejectWithValue }) => {
  try {
    const response = await resetPasswordAPI(payload);
    return response;
  } catch (err: any) {
    return rejectWithValue(err?.message || "Failed to reset password");
  }
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        loginUser.fulfilled,
        (
          state,
          action: PayloadAction<{
            status: boolean;
            message: string;
            data: { token: string };
          }>
        ) => {
          state.token = action.payload.data.token;
          state.user = null; // update if you later get user info
          state.loading = false;

          localStorage.setItem("token", action.payload.data.token);
        }
      )

      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string; // Store error message
      })

      // logout user
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // forgot password
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })

      // reset password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(
        resetPassword.fulfilled,
        (state, action: PayloadAction<LogoutResponse>) => {
          state.loading = false;
          state.message = action.payload.message;
          state.error = null;
        }
      )
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to reset password";
      });
  },
});

export default authSlice.reducer;
